import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { Prescription } from "@/modules/patients/detail/prescription/ch/schema/prescription-schema"

type State = {
  unSavedMedicationIds: string[]
  removedMedicationIds: string[]
  unSavedPrescriptionData: Omit<Prescription, "medicationIds"> | null
  suggestedMedications: string[]
}

type Actions = {
  setUnSavedMedicationIds: (medicationId: string) => void
  setRemovedMedicationIds: (medicationId: string) => void
  removeUnSavedMedicationId: (medicationId: string) => void
  setUnSavedPrescriptionData: (prescriptionData: Omit<Prescription, "medicationIds">) => void
  setSuggestedMedications: (suggestedMedications: string[]) => void
  reset: () => void
}

const initialState: State = {
  unSavedMedicationIds: [],
  removedMedicationIds: [],
  unSavedPrescriptionData: null,
  suggestedMedications: [],
}

const createChPrescriptionStore = () =>
  create<State & Actions>((set, get) => ({
    ...initialState,
    setUnSavedMedicationIds: (medicationId) =>
      set({ unSavedMedicationIds: [...get().unSavedMedicationIds, medicationId] }),
    setRemovedMedicationIds: (medicationId) =>
      set({ removedMedicationIds: [...get().removedMedicationIds, medicationId] }),
    removeUnSavedMedicationId: (medicationId) => {
      const unSavedMedicationIds = get().unSavedMedicationIds.filter((id) => id !== medicationId)
      set({ unSavedMedicationIds })
    },
    setUnSavedPrescriptionData: (prescriptionData) => set({ unSavedPrescriptionData: prescriptionData }),
    setSuggestedMedications: (suggestedMedications) => set({ suggestedMedications }),
    reset: () => set(initialState),
  }))

type ChPrescriptionStoreType = ReturnType<typeof createChPrescriptionStore>

export const ChPrescriptionStoreContext = createContext<ChPrescriptionStoreType | null>(null)

type ChPrescriptionStoreProviderProps = React.PropsWithChildren

export const ChPrescriptionStoreProvider = ({ children }: ChPrescriptionStoreProviderProps) => {
  const storeRef = useRef<ChPrescriptionStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createChPrescriptionStore()
  }
  return <ChPrescriptionStoreContext.Provider value={storeRef.current}>{children}</ChPrescriptionStoreContext.Provider>
}

export function useChPrescriptionStoreContext<T>(selector: (state: State & Actions) => T): T {
  const store = useContext(ChPrescriptionStoreContext)
  if (!store) throw new Error("Missing PrescriptionStoreProvider in the tree")
  return useStore(store, selector)
}
